import React, { useEffect, useCallback } from "react";
import { extractTextFromPdf } from "../../utils/fileProcessing";
import { extractPDFData } from "./configs";

/**
 * Specialized PDF Reader Component
 *
 * Handles PDF file processing with text extraction and pattern-based data extraction.
 *
 * @param {Object} props - Component props
 * @param {File} props.file - The PDF file to process
 * @param {Object} props.config - Configuration object for parsing
 * @param {Function} props.onDataParsed - Callback when data is successfully parsed
 * @param {Function} props.onError - Callback when parsing fails
 * @param {Function} props.onPdfUrlUpdate - Callback to update PDF URL for viewer
 * @param {Function} props.onExtractedTextUpdate - Callback to update extracted text
 */
const PDFReader = ({
  file,
  config,
  onDataParsed,
  onError,
  onPdfUrlUpdate,
  onExtractedTextUpdate,
}) => {
  /**
   * Process PDF file
   */
  const processPDF = useCallback(() => {
    if (!file) return;

    const reader = new FileReader();

    reader.onload = async (e) => {
      try {
        const arrayBuffer = e.target.result;

        // Create PDF URL for viewer
        const blob = new Blob([arrayBuffer], { type: "application/pdf" });
        const url = URL.createObjectURL(blob);
        onPdfUrlUpdate?.(url);

        // Extract text from PDF
        const text = await extractTextFromPdf(arrayBuffer);
        onExtractedTextUpdate?.(text);

        // Extract structured data using patterns
        const extractedData = extractPDFData(text, config);

        // Console summary (no raw values) to quickly see "final extraction result" in DevTools.
        // NOTE: Avoids logging extracted text or PII; only logs counts/keys.
        try {
          console.info("[PDFReader] extraction complete", {
            fileName: file?.name,
            extractedTextLength: typeof text === "string" ? text.length : null,
            extractedDataType: Array.isArray(extractedData)
              ? "array"
              : typeof extractedData,
            extractedDataCount: Array.isArray(extractedData)
              ? extractedData.length
              : null,
            extractedDataKeys:
              extractedData && typeof extractedData === "object"
                ? Object.keys(extractedData).slice(0, 40)
                : null,
          });
        } catch {
          // best-effort logging only
        }

        // Call success callback
        onDataParsed?.(extractedData, { text, arrayBuffer });
      } catch (error) {
        console.error("PDF processing error:", error);
        onError?.(error);
      }
    };

    reader.onerror = () => {
      onError?.(new Error("Failed to read PDF file"));
    };

    reader.readAsArrayBuffer(file);
  }, [
    file,
    config,
    onDataParsed,
    onError,
    onPdfUrlUpdate,
    onExtractedTextUpdate,
  ]);

  // Auto-process when file is provided
  useEffect(() => {
    if (file) {
      processPDF();
    }
  }, [file, processPDF]);

  // This component doesn't render anything - it's purely functional
  return null;
};

export default PDFReader;
